import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { getPriceBoard } from "@/lib/prices.functions";
import { formatMoney, type PriceRow } from "@/lib/prices-shared";
import { RarityBadge } from "@/components/RarityBadge";
import { cardLinkProps } from "@/lib/card-link";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, toolSchema } from "@/lib/schema";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { pageTitle } from "@/lib/seo-title";

const URL = "https://palstars.com/tcg-sold-prices";

const FAQS: FaqItem[] = [
  {
    q: "What are Palworld TCG cards actually selling for?",
    a: "This feed lists the average of recent completed TCGplayer sales for every Palworld printing that has sold, next to the current lowest asking price. Sold averages are money that changed hands, so they are the closest thing to a real market value for a Palworld card.",
  },
  {
    q: "Where does the sold data come from?",
    a: "Validated completed TCGplayer sales, refreshed by the daily Pal Stars price sync. Bulk lots, playsets, proxies, sealed product and implausible outliers are rejected before a sale is stored.",
  },
  {
    q: "Why are there no eBay sold prices?",
    a: "eBay declined Pal Stars' request for completed-sale (Marketplace Insights) API access, so no eBay sold value is displayed or estimated anywhere on the site. eBay columns elsewhere on Pal Stars carry active asking listings only.",
  },
  {
    q: "What does it mean when a card sells below its asking price?",
    a: "A negative sold-versus-ask figure means buyers are paying less than sellers are currently asking — usually a sign that the listed prices are ahead of demand. A positive figure means the card is selling at or above the cheapest listing, which is what a rising card looks like.",
  },
  {
    q: "Can I download the Palworld sold price data?",
    a: "Yes. The same rows are published as a free JSON feed at /api/public/card-sold.json, licensed for quoting and republishing with attribution and a link to palstars.com.",
  },
];

export const Route = createFileRoute("/tcg-sold-prices")({
  // Server-side render from the real price board so first paint (crawler and
  // browser alike) matches the hydrated client render.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: pageTitle("Palworld TCG Sold Prices — Real Sale Data") },
      {
        name: "description",
        content:
          "What Palworld TCG cards actually sell for: completed TCGplayer sale averages, sale counts and sold-versus-asking spread for every printing, refreshed daily.",
      },
      { property: "og:title", content: "Palworld TCG Sold Prices — Completed Sales" },
      {
        property: "og:description",
        content: "Completed-sale averages for Palworld OFFICIAL CARD GAME singles, updated daily from validated TCGplayer sales.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["TCG Price Chart", "/prices"],
        ["Sold Prices", "/tcg-sold-prices"],
      ]),
      toolSchema({
        name: "Palworld TCG sold price feed",
        description: "Completed-sale averages for Palworld card singles, sourced from validated TCGplayer sales.",
        path: "/tcg-sold-prices",
      }),
      faqSchema(FAQS, URL),
    ],
  }),
  component: SoldPricesPage,
});

type SortKey = "sold" | "recent" | "spread" | "count";

function SoldPricesPage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
  });

  const [sort, setSort] = useState<SortKey>("sold");
  const [query, setQuery] = useState("");

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sold = (data?.rows ?? []).filter((r) => r.tcgSoldCount > 0 && r.tcgSoldAvg !== null);
    const spread = (r: PriceRow) =>
      r.tcgMarket && r.tcgSoldAvg ? ((r.tcgSoldAvg - r.tcgMarket) / r.tcgMarket) * 100 : 0;
    return sold
      .filter((r) => !q || r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q))
      .sort((a, b) =>
        sort === "sold"
          ? (b.tcgSoldAvg ?? 0) - (a.tcgSoldAvg ?? 0)
          : sort === "count"
            ? b.tcgSoldCount - a.tcgSoldCount
            : sort === "spread"
              ? spread(b) - spread(a)
              : (b.soldLastAt ?? "").localeCompare(a.soldLastAt ?? ""),
      );
  }, [data?.rows, query, sort]);

  const updated = data?.updated ? new Date(data.updated).toUTCString() : "—";

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">Home</Link> ·{" "}
        <Link to="/prices" className="hover:text-primary">TCG Price Chart</Link> · <span>Sold Prices</span>
      </nav>

      <header>
        <h1 className="text-3xl font-extrabold sm:text-4xl">Palworld TCG Sold Prices</h1>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          What Palworld cards actually sell for. Every row is the average of recent completed TCGplayer sales for that
          printing, next to the cheapest current asking price so you can see whether listings are ahead of demand.
          Completed-sale data comes from TCGplayer only — eBay does not grant sold-data access.
        </p>
        <p className="mt-2 text-xs text-muted-foreground">Prices updated as of {updated}.</p>
      </header>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="sold-search">Search sold cards</label>
        <input
          id="sold-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search card name or number…"
          className="min-w-0 flex-1 rounded-lg border border-border/60 bg-card/40 px-3 py-2 text-sm outline-none focus:border-primary/60"
        />
        <label className="sr-only" htmlFor="sold-sort">Sort sold cards</label>
        <select
          id="sold-sort"
          value={sort}
          onChange={(e) => setSort(e.target.value as SortKey)}
          className="rounded-lg border border-border/60 bg-card/40 px-3 py-2 text-sm"
        >
          <option value="sold">Highest sold average</option>
          <option value="count">Most sales recorded</option>
          <option value="spread">Selling above asking</option>
          <option value="recent">Most recent sale</option>
        </select>
      </div>

      <div className="glass mt-4 overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[640px] text-sm">
          <caption className="sr-only">
            Palworld TCG cards with completed TCGplayer sales: sold average, number of sales, lowest asking price and
            sold-versus-asking spread
          </caption>
          <thead className="text-xs uppercase text-muted-foreground">
            <tr className="border-b border-border/60">
              <th scope="col" className="px-3 py-2 text-left">Card</th>
              <th scope="col" className="px-3 py-2 text-right">Sold avg</th>
              <th scope="col" className="px-3 py-2 text-right">Sales</th>
              <th scope="col" className="hidden px-3 py-2 text-right sm:table-cell">TCGplayer ask</th>
              <th scope="col" className="px-3 py-2 text-right">Sold vs ask</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const spread =
                r.tcgMarket && r.tcgSoldAvg
                  ? Math.round(((r.tcgSoldAvg - r.tcgMarket) / r.tcgMarket) * 1000) / 10
                  : null;
              return (
                <tr key={r.code} className="border-b border-border/40 last:border-0">
                  <td className="px-3 py-2">
                    <Link {...cardLinkProps(r.code)} className="font-semibold hover:text-primary">
                      {r.name}
                    </Link>
                    <span className="ml-2 text-xs text-muted-foreground">{r.code}</span>
                    <span className="ml-2 align-middle"><RarityBadge code={r.code} /></span>
                  </td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums">
                    {formatMoney(r.tcgSoldAvg ?? 0, "USD")}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{r.tcgSoldCount}</td>
                  <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">
                    {r.tcgMarket ? formatMoney(r.tcgMarket, "USD") : "—"}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {spread === null ? (
                      "—"
                    ) : (
                      <span className={spread >= 0 ? "text-emerald-400" : "text-rose-400"}>
                        {spread > 0 ? "+" : ""}
                        {spread.toFixed(1)}%
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">
            No completed sales are stored yet for this filter. The daily sync adds sales as TCGplayer records them.
          </p>
        ) : null}
      </div>

      <p className="mt-4 text-xs text-muted-foreground">
        Free JSON feed:{" "}
        <a href="/api/public/card-sold.json" className="text-primary hover:underline">
          /api/public/card-sold.json
        </a>{" "}
        · Bulk buy a list of these cards on the{" "}
        <Link to="/tcg-bulk-order" className="text-primary hover:underline">
          Palworld bulk card order builder
        </Link>
        .
      </p>

      <div className="mt-6">
        <AffiliateDisclosure />
      </div>

      <FaqSection items={FAQS} />
    </main>
  );
}
